import { useQuery } from "@tanstack/react-query";
import { parseISO, startOfToday } from "date-fns";
import { MapPin, Star } from "lucide-react";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, money, toCriteria } from "../lib/api";
import { useBooking } from "../context/BookingContext";
import { useSitePath } from "../context/SiteContext";
import { StepLayout } from "../components/StepLayout";
import { AmenityList } from "../components/Amenities";
import { useLocations } from "../components/SearchPanel";
import { SafeImg } from "../components/SafeImg";
import { Skeleton } from "../components/ui/skeleton";

const HotelCard = ({ h, currency, onSelect }) => (
  <article className={`group grid grid-cols-[minmax(0,1fr)] overflow-hidden rounded-2xl border border-brand/10 bg-white md:grid-cols-[300px_minmax(0,1fr)] ${h.available ? "" : "opacity-60"}`} data-testid={`hotel-card-${h.id}`}>
    <div className="relative h-56 overflow-hidden md:h-full">
      <SafeImg src={h.image} alt={h.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
    </div>
    <div className="flex flex-col p-6">
      <div className="flex items-center gap-1 text-gold">{Array.from({ length: h.stars }).map((_, k) => <Star key={k} className="h-3.5 w-3.5 fill-current" />)}</div>
      <h3 className="mt-2 font-serif text-3xl text-ink">{h.name}</h3>
      <p className="mt-1 flex items-center gap-1 text-xs text-ink/50"><MapPin className="h-3.5 w-3.5" />{h.city}, {h.country}</p>
      <p className="mt-3 text-sm text-ink/70">{h.tagline}</p>
      <AmenityList features={h.features} limit={5} className="mt-4" />
      <div className="mt-6 flex flex-wrap items-end justify-between gap-4 border-t border-ink/10 pt-4">
        {h.available ? (
          <div>
            <p className="text-xs text-ink/50">From</p>
            <p className="font-mono text-2xl text-ink" data-testid={`hotel-from-price-${h.id}`}>{money(h.from_price, currency)}<span className="text-xs text-ink/50"> / night</span></p>
          </div>
        ) : <p className="text-sm text-ink/60" data-testid={`hotel-unavailable-${h.id}`}>Not available for your dates</p>}
        <button disabled={!h.available} onClick={onSelect} className="btn-primary disabled:opacity-40" data-testid={`hotel-view-rates-${h.id}`}>View rates</button>
      </div>
    </div>
  </article>
);

const useSearchFromLink = (setCriteria) => {
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const a = params.get("arrival");
    if (!a) return;
    if (/^\d{4}-\d{2}-\d{2}$/.test(a) && parseISO(a) >= startOfToday()) {
      const n = (k, lo, hi, d) => Math.min(hi, Math.max(lo, Number(params.get(k)) || d));
      const flag = (k, d) => (params.has(k) ? params.get(k) === "1" : d);
      setCriteria({ arrival: a, nights: n("nights", 1, 30, 1), adults: n("adults", 1, 6, 2), rooms: n("rooms", 1, 5, 1), hotel_id: "", hotel_name: "",
        kids: Math.min(6, Math.max(0, Number(params.get("kids")) || 0)), promo: (params.get("promo") || "").slice(0, 30),
        show_packages: flag("show_packages", true), packages_only: flag("packages_only", false) });
    }
    setParams({}, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export default function Hotels() {
  const { criteria, selectHotel, setCriteria } = useBooking();
  const navigate = useNavigate();
  const sp = useSitePath();
  useSearchFromLink(setCriteria);
  const { data, isLoading, error } = useQuery({
    queryKey: ["availability", criteria],
    queryFn: () => api.post("/availability", toCriteria(criteria)).then((r) => r.data),
  });
  const pick = (h, replace = false) => {
    selectHotel(h);
    navigate(sp(`/rates/${h.id}`), { replace });
  };
  const { data: loc } = useLocations();
  const single = loc?.hotels?.length === 1 && data?.hotels.length === 1 && data.hotels[0].available ? data.hotels[0] : null;
  useEffect(() => {
    if (single) {
      pick(single, true);
    }
  }, [single]); // eslint-disable-line react-hooks/exhaustive-deps
  if (single) return <StepLayout step={2}><Skeleton className="h-64 rounded-2xl" /></StepLayout>;
  return (
    <StepLayout step={1}>
      <h1 className="font-serif text-4xl text-ink sm:text-5xl">Choose your hotel</h1>
      <p className="mt-2 text-sm text-ink/60" data-testid="hotels-count">{data ? `${data.hotels.length} hotel(s) found` : "Checking availability…"}</p>
      <div className="mt-8 space-y-6" data-testid="hotels-list">
        {isLoading && [0, 1].map((k) => <Skeleton key={k} className="h-64 rounded-2xl" />)}
        {error && <p className="text-red-600" data-testid="hotels-error">{apiError(error)}</p>}
        {data?.hotels.length === 0 && <p className="rounded-2xl bg-white p-8 text-ink/60" data-testid="hotels-empty">No hotels match your search. Try another country or city.</p>}
        {data?.hotels.map((h) => <HotelCard key={h.id} h={h} currency={data.currency} onSelect={() => pick(h)} />)}
      </div>
    </StepLayout>
  );
}
